import { AlertTriangle, FlaskConical } from 'lucide-react';

import type { RxMedication } from '@/entities/rx';
import { cn } from '@/lib/utils';

export interface MedicationMetaBadgesProps {
  medication: RxMedication | null | undefined;
}

/** Only surface medication metadata that requires clinician attention. */
export function MedicationMetaBadges({ medication }: MedicationMetaBadgesProps) {
  if (!medication) {
    return null;
  }

  const classifications = collectClassifications(medication);
  const items = collectBadges(medication);
  if (classifications.length === 0 && items.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1.5 text-xs text-muted-foreground">
      {classifications.length > 0 ? <div>{classifications.join(' · ')}</div> : null}
      {items.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {items.map((item) => (
            <span key={item.key} className={cn('inline-flex items-center gap-1.5', item.tone)}>
              {item.icon}
              {item.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

interface BadgeItem {
  key: string;
  label: string;
  tone: string;
  icon: React.ReactNode;
}

const TONE_WARN = 'text-amber-700 dark:text-amber-300';
const TONE_DANGER = 'font-medium text-red-700 dark:text-red-300';

function collectClassifications(medication: RxMedication): string[] {
  const classifications: string[] = [];
  if (medication.Brand === true) {
    classifications.push('Brand name');
  }
  if (medication.Brand === false) {
    classifications.push('Generic');
  }
  if (medication.OTC === true) {
    classifications.push('Over-the-counter');
  }
  if (medication.OTC === false) {
    classifications.push('Prescription medication');
  }
  return classifications;
}

function collectBadges(medication: RxMedication): BadgeItem[] {
  const badges: BadgeItem[] = [];

  const schedule = scheduleLabel(medication.Schedule);
  if (schedule != null) {
    badges.push({
      key: 'schedule',
      label: schedule,
      tone: TONE_WARN,
      icon: <FlaskConical className="size-3" aria-hidden />,
    });
  }

  if (medication.IsObsolete === true) {
    badges.push({
      key: 'obsolete',
      label: 'No longer marketed',
      tone: TONE_DANGER,
      icon: <AlertTriangle className="size-3" aria-hidden />,
    });
  }

  return badges;
}

/**
 * Map DoseSpot's integer ``Schedule`` (1-5 for federal schedule) to a
 * Roman-numeral label. Schedule 0 / null = "not scheduled" → no badge.
 */
function scheduleLabel(schedule: number | null | undefined): string | null {
  if (schedule == null || schedule <= 0 || schedule > 5) {
    return null;
  }
  const roman = ['I', 'II', 'III', 'IV', 'V'][schedule - 1];
  return `Schedule ${roman} controlled substance`;
}
